import { nothing } from 'lit-html';
import { html, property } from 'lit-element';
import { KatLitElement, Keys, register } from '../../shared/base';

import baseStyles from '../../shared/base/base.lit.scss';
import styles from './dropdown-button.lit.scss';

/**
 * @component {kat-dropdown-button} KatalDropdownbutton A button that groups a primary action together with related secondary actions accessible via a dropdown menu. Use this for consolidating actions that can be decided upon by the user.
 * @example Basic {"variant": "primary", "size": "base", "disabled": false, "responsive": false, "options": [{"label":"Button text","action":"action1"},{"label":"Option 1","action":"action2"},{"label":"Option 2","action":"action3"}]}
 * @example Small {"variant": "primary", "size": "small", "disabled": false, "responsive": false, "options": [{"label":"Small","action":"action1"},{"label":"Option 1","action":"action2"},{"label":"Option 2","action":"action3"}]}
 * @example Disabled {"variant": "primary", "size": "small", "disabled": true, "responsive": false, "options": [{"label":"Disabled","action":"action1"},{"label":"Option 1","action":"action2"},{"label":"Option 2","action":"action3"}]}
 * @example Secondary {"variant": "secondary", "size": "base", "disabled": false, "responsive": false, "options": [{"label":"Secondary","action":"action1"},{"label":"Option 1","action":"action2"},{"label":"Option 2","action":"action3"}]}
 * @event expand This event is fired whenever the dropdown options expand open.
 * @event collapse This event is fired whenever the dropdown options are hidden.
 * @event action This event is fired whenever the action is clicked
 * @status Production
 */
@register('kat-dropdown-button')
export class KatalDropdownbutton extends KatLitElement {
  /**
   * @classprop {json} options Array of objects [{"label":"string","action":"string"}]
   * @required
   */
  @property()
  options: { label: string; action: string }[] = [];

  /**
   * @classprop {boolean} expanded Whether the component is currently showing its dropdown options.
   */
  @property()
  expanded = false;

  /**
   * @classprop {enum} size Specifies the visual size of the button.  Defaults to "base"
   * @enum {value} base This is the default button size.
   * @enum {value} small This is a smaller button
   * @required
   */
  @property()
  size = 'base';

  /**
   * @classprop {boolean} disabled If present, the button is not clickable.
   */
  @property()
  disabled: boolean;

  /**
   * @classprop {enum} variant Specifies the type of button to be displayed.  Defaults to primary
   * @enum {value} primary This is the default button type.
   * @enum {value} secondary some other kind button
   * @required
   */
  @property()
  variant = 'primary';

  /**
   * @classprop {boolean} responsive If present, the button will adjust its appearance on smaller screens
   */
  @property()
  responsive: boolean;

  static get styles() {
    return [baseStyles, styles];
  }

  constructor() {
    super();

    this.addEventListener('focusout', e => {
      const newFocus = e.relatedTarget;

      if (!this.disabled && !this.contains(newFocus) && this.expanded) {
        this.toggleExpanded();
      }
    });
  }

  updated(changedProps) {
    if (changedProps.has('disabled')) {
      if (this.disabled) {
        this.expanded = false;
      }
    }
  }

  render() {
    const options = Array.isArray(this.options)
      ? this.options.slice(1).map((option, i) => {
          const action = option.action || '';
          const label = option.label || '';
          return html`
            <button
              part="dropdown-button-button-${i + 1}"
              data-action=${action}
              role="menuitem"
              class="option"
            >
              ${label}
            </button>
          `;
        })
      : nothing;
    return html`
      <div class="button-group-header">
        <button
          class="button"
          part="dropdown-button-button-0"
          type="button"
          data-action=${this.getFirstAction()}
          ?disabled=${this.disabled}
          @click=${this._firstOptionClickHandler}
        >
          <span>${this.getFirstLabel()}</span>
        </button>
        <button
          part="dropdown-button-toggle-button"
          type="button"
          class="indicator"
          ?disabled=${this.disabled}
          @click=${this._toggleClickHandler}
          @keydown=${this._toggleKeydownHandler}
          role="menuitem"
          aria-haspopup="true"
          aria-expanded=${this.expanded}
        >
          <kat-icon name="chevron-down" size="small"></kat-icon>
        </button>
      </div>
      <div
        class="options"
        part="dropdown-button-options"
        role="menu"
        @click=${this._listItemClickHandler}
        @keydown=${this._listKeydownHandler}
      >
        ${options}
      </div>
    `;
  }

  getFirstLabel() {
    return this.options && this.options.length > 0
      ? this.options[0].label || ''
      : '-- missing option';
  }

  getFirstAction() {
    return this.options && this.options.length > 0
      ? this.options[0].action || ''
      : '';
  }

  _toggleClickHandler(evt) {
    if (!this.disabled) {
      this.toggleExpanded();
    }
  }

  _toggleKeydownHandler(e) {
    if (e.which === Keys.Escape) {
      if (this.expanded) {
        this.toggleExpanded();
      }
    } else if (e.which === Keys.ArrowDown) {
      if (!this.expanded) {
        this.toggleExpanded();
      }

      this._moveMenuOption(1, null);
    }
  }

  _firstOptionClickHandler(evt) {
    if (!this.disabled) {
      this.triggerAction(evt.target);
    }
  }

  _listKeydownHandler(e) {
    if (e.which === Keys.ArrowDown) {
      this._moveMenuOption(1, e.target);
    } else if (e.which === Keys.ArrowUp) {
      this._moveMenuOption(-1, e.target);
    } else if (e.which === Keys.Escape) {
      if (this.expanded) {
        this.toggleExpanded();
      }
    }
  }

  _listItemClickHandler(e) {
    if (!this.disabled) {
      this.triggerAction(e.target);
    }
  }

  _moveMenuOption(increment, optionsElement) {
    const items = this.shadowRoot.querySelectorAll('.options button');

    if (items.length === 0) {
      return;
    }

    let index = Array.from(items).indexOf(optionsElement);

    if (increment < 0 && index > 0) {
      // Up
      index -= 1;
    }

    if (increment > 0 && index < items.length - 1) {
      // Down
      index += 1;
    }

    items[index].focus();
  }

  toggleExpanded() {
    this.expanded = !this.expanded;

    this.dispatchCustomEvent(this.expanded ? 'expand' : 'collapse');
  }

  triggerAction(element) {
    const button = element.closest('button');
    const action = button.dataset['action'];
    this.dispatchCustomEvent('action', { action });

    if (this.expanded) {
      this.toggleExpanded();
      // Refocus the toggle button
      this._shadow('.indicator').focus();
    }
  }
}
